import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { onSnapshot } from "firebase/firestore";
import { Button, Form, Header, Loader, Message, Segment } from "semantic-ui-react";
import ModalWrapper from "../../../../app/common/modals/modalWrapper";
import { closeModal, openModal } from "../../../../app/common/modals/modalSlice";
import { fetchDefaultComplianceTemplatesByStateFromDb } from "../../../../app/firestore/firestoreService";

export default function BrokerageComplianceNewTemplateModal({
  brokerageId,
  stateProvince,
  brokerageTemplates = [],
}) {
  const dispatch = useDispatch();
  const [selected, setSelected] = useState("blank");
  const [stateDefaults, setStateDefaults] = useState([]);
  const [loadingDefaults, setLoadingDefaults] = useState(false);

  useEffect(() => {
    if (!stateProvince) return undefined;
    setLoadingDefaults(true);
    const q = fetchDefaultComplianceTemplatesByStateFromDb(stateProvince);
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) =>
            (a.agentRepresents || "").localeCompare(b.agentRepresents || "")
          );
        setStateDefaults(data);
        setLoadingDefaults(false);
      },
      () => {
        setStateDefaults([]);
        setLoadingDefaults(false);
      }
    );
    return () => unsubscribe();
  }, [stateProvince]);

  const findTemplate = (key) => {
    const [source, id] = key.split(":");
    if (source === "brokerage") return brokerageTemplates.find((t) => t.id === id);
    if (source === "default") return stateDefaults.find((t) => t.id === id);
    return null;
  };

  const handleContinue = () => {
    const baseModalProps = {
      existingTemplate: null,
      isEditMode: false,
      prefilledBrokerageId: brokerageId,
      prefilledStateProvince: stateProvince,
      lockBrokerageId: true,
      lockStateProvince: true,
    };
    if (selected === "blank") {
      dispatch(closeModal());
      dispatch(
        openModal({
          modalType: "AdminComplianceTemplateModal",
          modalProps: baseModalProps,
        })
      );
      return;
    }
    const template = findTemplate(selected);
    if (!template) return;
    dispatch(closeModal());
    dispatch(
      openModal({
        modalType: "AdminComplianceTemplateModal",
        modalProps: { ...baseModalProps, prefillFromTemplate: template },
      })
    );
  };

  const renderOption = (value, label, sub) => {
    const isSelected = selected === value;
    const labelStyle = {
      display: "block",
      padding: "8px 10px",
      cursor: "pointer",
      backgroundColor: isSelected ? "#e3f2fd" : "transparent",
      border: isSelected ? "1px solid #2185d0" : "1px solid transparent",
      borderRadius: 4,
    };
    return (
      <Form.Field key={value}>
        <label style={labelStyle}>
          <input
            type="radio"
            name="copySource"
            value={value}
            checked={isSelected}
            onChange={() => setSelected(value)}
            style={{ marginRight: 8 }}
          />
          <strong>{label}</strong>
          {sub && (
            <div style={{ fontSize: "0.85em", color: "#666", marginLeft: 24 }}>{sub}</div>
          )}
        </label>
      </Form.Field>
    );
  };

  return (
    <ModalWrapper size="small" header="New Compliance Template">
      <Form>
        <Segment basic>
          {renderOption("blank", "Start from a blank template")}

          <Header size="tiny" color="grey" style={{ marginTop: 16 }}>
            Copy from one of this brokerage's templates
          </Header>
          {brokerageTemplates.length === 0 ? (
            <Message size="tiny" info>No existing brokerage templates to copy from.</Message>
          ) : (
            brokerageTemplates.map((t) =>
              renderOption(`brokerage:${t.id}`, t.name,
                `${t.stateProvince} · ${t.agentRepresents || "—"} · ${t.categories?.length || 0} categories`)
            )
          )}

          <Header size="tiny" color="grey" style={{ marginTop: 16 }}>
            Copy from {stateProvince || "state"} default templates
          </Header>
          {loadingDefaults ? (
            <Loader active inline="centered" size="small">Loading defaults...</Loader>
          ) : stateDefaults.length === 0 ? (
            <Message size="tiny" info>No default templates available for {stateProvince || "this state"}.</Message>
          ) : (
            stateDefaults.map((t) =>
              renderOption(`default:${t.id}`, t.name,
                `${t.agentRepresents || "—"} · ${t.categories?.length || 0} categories`)
            )
          )}
        </Segment>
        <Segment basic textAlign="right">
          <Button type="button" onClick={() => dispatch(closeModal())}>
            Cancel
          </Button>
          <Button type="button" color="blue" onClick={handleContinue}>
            Continue
          </Button>
        </Segment>
      </Form>
    </ModalWrapper>
  );
}
